import { useEffect, useState } from "react";
import { Cookie, Settings, Shield, BarChart3, Target, CheckCircle, X } from "lucide-react";
import useThemeStore from "../store/themeStore";
import useTokenStore from "../store/store";
import { updatePrivacySettings } from "../utils/user";
import { ThemeProps } from "../utils/theme";

// Add type declarations for global functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    clarity: any;
  }
}

interface CookieCategory {
  key: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  enabled: boolean;
  readOnly?: boolean;
}

// Add this function to sync stored consent when user logs in
export const syncStoredConsentToBackend = async (token: string) => {
  try {
    const consent = localStorage.getItem('cc_prefs');
    if (consent) {
      const parsed = JSON.parse(consent);
      if (parsed.acceptedCategories) {
        const updated = {
          essential: true,
          functional: parsed.acceptedCategories.includes("functional"),
          analytics: parsed.acceptedCategories.includes("analytics"),
          marketing: parsed.acceptedCategories.includes("marketing"),
          version: parsed.version || getPrivacyPolicyVersion(),
          method: parsed.method || 'banner',
        };
        
        console.log(" Syncing stored consent to backend:", updated);
        await updatePrivacySettings(token, updated);
        console.log("✅ Stored consent synced successfully");
      }
    }
  } catch (error) {
    console.error('❌ Failed to sync stored consent:', error);
  }
};

// Helper function to get privacy policy version (make it available outside component)
export const getPrivacyPolicyVersion = () => {
  return process.env.REACT_APP_PRIVACY_VERSION || '2025.01';
};

export default function CookieConsentManager() {
  const theme = useThemeStore((state) => state.theme) as ThemeProps;
  const token = useTokenStore((state) => state.token);
  
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [categories, setCategories] = useState<CookieCategory[]>([
    {
      key: 'necessary',
      title: 'Strictly Necessary',
      description: 'Required for core functionality such as security, authentication, and basic site features. These cannot be disabled.',
      icon: Shield,
      enabled: true,
      readOnly: true
    },
    {
      key: 'functional',
      title: 'Functional',
      description: 'Remembers your preferences and settings (e.g. UI choices, language, theme).',
      icon: Settings,
      enabled: false
    },
    {
      key: 'analytics',
      title: 'Analytics',
      description: 'Helps us improve the site by collecting usage information and performance metrics.',
      icon: BarChart3,
      enabled: false
    },
    {
      key: 'marketing',
      title: 'Marketing',
      description: 'Used to show relevant recommendations and measure campaign effectiveness.',
      icon: Target,
      enabled: false
    }
  ]);

  const [initialLoad, setInitialLoad] = useState(true);

  // Helper functions
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const getContrastColor = (hex: string) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return '#000000';
    
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  // Check if consent has been given
  const checkExistingConsent = () => {
    const consent = localStorage.getItem('cc_prefs');
    if (consent) {
      try {
        const parsed = JSON.parse(consent);
        if (parsed.acceptedCategories) {
          setCategories(prev => prev.map(cat => ({
            ...cat,
            enabled: cat.readOnly ? true : parsed.acceptedCategories.includes(cat.key)
          })));
          return true;
        }
      } catch (error) {
        console.error('Error parsing consent:', error);
      }
    }
    return false;
  };

  // Sync consent to backend and analytics with full tracking
  const syncToBackend = async (acceptedCategories: string[], method: 'banner' | 'settings') => {
    try {
      if (token) {
        const updated = {
          essential: true,
          functional: acceptedCategories.includes("functional"),
          analytics: acceptedCategories.includes("analytics"),
          marketing: acceptedCategories.includes("marketing"),
          
          // Add consent tracking metadata
          version: getPrivacyPolicyVersion(),
          method: method,
          // Don't send IP/UA from frontend - let backend collect them for security
        };
        
        await updatePrivacySettings(token, updated);
      }
      
      // Update GA4 consent mode
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: acceptedCategories.includes('analytics') ? 'granted' : 'denied',
          ad_storage: acceptedCategories.includes('marketing') ? 'granted' : 'denied',
          ad_user_data: acceptedCategories.includes('marketing') ? 'granted' : 'denied',
          ad_personalization: acceptedCategories.includes('marketing') ? 'granted' : 'denied',
          functionality_storage: acceptedCategories.includes('functional') ? 'granted' : 'denied',
          security_storage: 'granted'
        });
      }

      // Update Clarity consent
      if (window.clarity) {
        window.clarity('consent', acceptedCategories.includes('analytics'));
      }
    } catch (error) {
      console.error('Failed to sync consent to backend:', error);
    }
  };

  // Save consent preferences with method tracking
  const saveConsent = (acceptedCategories: string[], method: 'banner' | 'settings' = 'banner') => {
    const consent = {
      acceptedCategories,
      timestamp: Date.now(),
      version: getPrivacyPolicyVersion(),
      method: method
    };
    
    localStorage.setItem('cc_prefs', JSON.stringify(consent));
    syncToBackend(acceptedCategories, method);
  };

  // Handle accept all
  const handleAcceptAll = () => {
    const allCategories = categories.map(cat => cat.key);
    setCategories(prev => prev.map(cat => ({ ...cat, enabled: true })));
    const method = isPreferencesModalOpen ? 'settings' : 'banner';
    saveConsent(allCategories, method);
    setIsConsentModalOpen(false);
    setIsPreferencesModalOpen(false);
  };

  // Handle reject all (except necessary)
  const handleRejectAll = () => {
    const necessaryOnly = ['necessary'];
    setCategories(prev => prev.map(cat => ({ 
      ...cat, 
      enabled: cat.readOnly ? true : false 
    })));
    const method = isPreferencesModalOpen ? 'settings' : 'banner';
    saveConsent(necessaryOnly, method);
    setIsConsentModalOpen(false);
    setIsPreferencesModalOpen(false);
  };

  // Handle save preferences
  const handleSavePreferences = () => {
    const acceptedCategories = categories.filter(cat => cat.enabled).map(cat => cat.key);
    saveConsent(acceptedCategories, 'settings'); // Always 'settings' when saving from preferences modal
    setIsPreferencesModalOpen(false);
    setHasChanges(false);
  };

  // Handle category toggle
  const handleCategoryToggle = (categoryKey: string) => {
    if (categories.find(cat => cat.key === categoryKey)?.readOnly) return;
    
    setCategories(prev => prev.map(cat => 
      cat.key === categoryKey ? { ...cat, enabled: !cat.enabled } : cat
    ));
    setHasChanges(true);
  };

  // Initialize on mount
  useEffect(() => {
    if (!theme || !initialLoad) return;
    
    const hasExistingConsent = checkExistingConsent();
    if (!hasExistingConsent) {
      setIsConsentModalOpen(true);
    }
    setInitialLoad(false);
  }, [theme, initialLoad]);

  // Make showCookiePreferences globally available
  useEffect(() => {
    (window as any).showCookiePreferences = () => {
      setIsPreferencesModalOpen(true);
    };
  }, []);

  if (!theme) return null;

  const primaryColor = theme.color1 || '#1e1b4b';
  const secondaryColor = theme.color2 || '#4f46e5';
  const primaryTextColor = getContrastColor(primaryColor);
  const secondaryTextColor = getContrastColor(secondaryColor);

  // Button styles - USING YOUR THEME COLORS AND FONT
  const primaryButtonStyle = {
    backgroundColor: primaryColor,
    color: primaryTextColor,
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontWeight: '600',
    fontFamily: 'Poppins, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const secondaryButtonStyle = {
    backgroundColor: '#f3f4f6', // YOUR GREY BASE
    color: '#000000', // YOUR BLACK TEXT
    border: '1px solid #d1d5db',
    padding: '10px 22px',
    borderRadius: '8px',
    fontWeight: '500',
    fontFamily: 'Poppins, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const saveButtonStyle = {
    backgroundColor: hasChanges ? primaryColor : '#e5e7eb',
    color: hasChanges ? primaryTextColor : '#9ca3af',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontWeight: '600',
    fontFamily: 'Poppins, sans-serif',
    cursor: hasChanges ? 'pointer' : 'not-allowed',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  // Consent Modal
  const ConsentModal = () => (
    <div 
      className="fixed inset-0 z-[10000] flex items-end justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white rounded-t-xl shadow-2xl max-w-2xl w-full transform transition-transform duration-300"
        style={{ 
          maxHeight: '90vh',
          backgroundColor: 'white'
        }}
      >
        <div className="p-6">
          <div className="flex items-center mb-4">
            <Cookie className="w-8 h-8 mr-3" style={{ color: '#000000' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#000000' }}>We use cookies</h2>
          </div>
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            We use essential cookies to make our site work. We'd also like to use optional cookies 
            to improve your experience and provide personalized content. You can accept all, reject all, 
            or set your preferences.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAcceptAll}
              style={primaryButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = secondaryColor;
                e.currentTarget.style.color = secondaryTextColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.color = primaryTextColor;
              }}
            >
              <CheckCircle className="w-4 h-4" />
              Accept All
            </button>
            
            <button
              onClick={handleRejectAll}
              style={secondaryButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.color = primaryTextColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#000000';
              }}
            >
              <X className="w-4 h-4" />
              Reject All
            </button>
            
            <button
              onClick={() => {
                setIsConsentModalOpen(false);
                setIsPreferencesModalOpen(true);
              }}
              style={secondaryButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.color = primaryTextColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#000000';
              }}
            >
              <Settings className="w-4 h-4" />
              Manage Preferences
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              <a 
                href="/privacy-policy" 
                target="_blank" 
                className="hover:underline"
                style={{ color: primaryColor }}
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Preferences Modal
  const PreferencesModal = () => (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        style={{ backgroundColor: 'white' }}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Settings className="w-6 h-6 mr-3" style={{ color: '#000000' }} />
              <h2 className="text-xl font-bold" style={{ color: '#000000' }}>Manage Cookie Preferences</h2>
            </div>
            <button
              onClick={() => {
                setIsPreferencesModalOpen(false);
                setHasChanges(false);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <div 
                  key={category.key}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="mt-1">
                        <IconComponent 
                          className="w-5 h-5 text-black"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{category.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{category.description}</p>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <button
                        onClick={() => handleCategoryToggle(category.key)}
                        disabled={category.readOnly}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          category.readOnly ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        style={{
                          backgroundColor: category.enabled ? primaryColor : '#d1d5db',
                        }}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            category.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={handleAcceptAll}
              style={primaryButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = secondaryColor;
                e.currentTarget.style.color = secondaryTextColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.color = primaryTextColor;
              }}
            >
              Accept All
            </button>
            
            <button
              onClick={handleRejectAll}
              style={secondaryButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.color = primaryTextColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#000000';
              }}
            >
              Reject All
            </button>
            
            <button
              onClick={handleSavePreferences}
              disabled={!hasChanges}
              style={saveButtonStyle}
              onMouseEnter={(e) => {
                if (hasChanges) {
                  e.currentTarget.style.backgroundColor = secondaryColor;
                  e.currentTarget.style.color = secondaryTextColor;
                }
              }}
              onMouseLeave={(e) => {
                if (hasChanges) {
                  e.currentTarget.style.backgroundColor = primaryColor;
                  e.currentTarget.style.color = primaryTextColor;
                }
              }}
            >
              <CheckCircle className="w-4 h-4" />
              Save Choices
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Floating Cookie Settings Button - USING YOUR THEME COLORS AND FONT
  const FloatingButton = () => (
    <button
      onClick={() => setIsPreferencesModalOpen(true)}
      className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 px-3 py-2 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
      style={{
        backgroundColor: '#f3f4f6', // YOUR GREY BASE
        color: '#000000', // YOUR BLACK TEXT
        border: '1px solid #d1d5db',
        fontSize: '12px',
        fontFamily: 'Poppins, sans-serif'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = primaryColor;
        e.currentTarget.style.color = primaryTextColor;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#f3f4f6';
        e.currentTarget.style.color = '#000000';
      }}
    >
      <Cookie className="w-4 h-4" />
      Cookie Settings
    </button>
  );

  return (
    <>
      {isConsentModalOpen && <ConsentModal />}
      {isPreferencesModalOpen && <PreferencesModal />}
      <FloatingButton />
    </>
  );
}